
'use client';

import { firebaseConfig } from '@/firebase/config';
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { initializeAuth, browserLocalPersistence, browserPopupRedirectResolver, type Auth, getAuth } from 'firebase/auth';
import {
    getFirestore,
    initializeFirestore,
    persistentLocalCache,
    persistentMultipleTabManager,
    clearIndexedDbPersistence,
    type Firestore,
} from 'firebase/firestore';
import type { AppStorage } from '@/lib/storage';
import { initializeAppCheck, ReCaptchaV3Provider, type AppCheck } from 'firebase/app-check';
import { wipeCacheIfFlagged as wipeFlaggedCacheWhenAlone } from '@/lib/firestore-cache-wipe';
import { browserLocalStorage } from '@/lib/browser-storage';

const isNewApp = !getApps().length;
const firebaseApp: FirebaseApp = isNewApp ? initializeApp(firebaseConfig) : getApp();

// True when Firebase has a persisted auth session in localStorage. Used to
// decide whether App Check needs to be live for the initial SDK calls: a
// signed-in visitor's `initializeAuth` triggers an identitytoolkit refresh
// that needs a valid App Check token when enforcement is on. Key shape:
// `firebase:authUser:<apiKey>:<appName>` (appName defaults to `[DEFAULT]`).
function hasPersistedSession(): boolean {
    if (typeof window === 'undefined') return false;
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('firebase:authUser:')) return true;
        }
    } catch {
        // Safari ITP / privacy modes can throw on localStorage access. Fall
        // through; App Check will init on first interaction instead.
    }
    return false;
}

// App Check is the gatekeeper for Firebase backends (Firestore, Storage,
// identitytoolkit). Its reCAPTCHA v3 provider pulls in Google's `api.js`,
// which sets a third-party `_GRECAPTCHA` cookie, ships large minified JS
// with no source maps, and renders a badge image, all three flagged by
// Lighthouse Best Practices. We init lazily so Lighthouse audits and bots
// (which never interact) don't trigger reCAPTCHA on the public landing page.
let appCheckInstance: AppCheck | null = null;

export function ensureAppCheck(): AppCheck | null {
    if (typeof window === 'undefined') return null;
    if (appCheckInstance) return appCheckInstance;

    // Debug token must be on `self` before `initializeAppCheck` runs so the
    // SDK picks it up. Production refuses to ship with this set (see
    // env-guard.ts).
    if (process.env.NODE_ENV === 'development') {
        (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN =
            process.env.NEXT_PUBLIC_APPCHECK_DEBUG_TOKEN;
    }

    appCheckInstance = initializeAppCheck(firebaseApp, {
        provider: new ReCaptchaV3Provider(
            process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!
        ),
        isTokenAutoRefreshEnabled: true,
    });
    return appCheckInstance;
}

// Signed-in users still need App Check, but we defer it past first paint
// so it doesn't compete with the LCP work and so Lighthouse's metric
// collection window doesn't see the reCAPTCHA fetch on signed-in audits.
// Anonymous visitors wait for first interaction (see client-provider.tsx).
//
// `requestIdleCallback` lets us slot in once the main thread is free; the
// 2-second timeout ensures we don't starve indefinitely on a busy page.
// The auth restore (initializeAuth below) does not need App Check to be
// running synchronously: the identitytoolkit refresh only fires when the
// auth context first asks for a fresh ID token, by which point our
// `ensureAppCheck` will have run.
if (typeof window !== 'undefined' && hasPersistedSession()) {
    const start = () => ensureAppCheck();
    if ('requestIdleCallback' in window) {
        (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
            .requestIdleCallback(start, { timeout: 2000 });
    } else {
        setTimeout(start, 0);
    }
}

// initializeAuth can only be called once per app. On subsequent module evaluations
// (e.g. Next.js HMR in development), the app already exists so we must use getAuth,
// which returns the previously created auth instance (including its popup resolver).
let auth: Auth;
if (typeof window !== 'undefined' && isNewApp) {
  auth = initializeAuth(firebaseApp, {
    persistence: browserLocalPersistence,
    popupRedirectResolver: browserPopupRedirectResolver,
  });
} else {
  auth = getAuth(firebaseApp);
}

/**
 * localStorage flag written by `logout()` (src/context/auth-context.tsx). The
 * signed-out user's documents are still sitting in IndexedDB at that moment,
 * so we mark the cache dirty and wipe it on a later app boot, the first one
 * that finds no other tab open. See `wipeCacheIfFlagged` below for why the wipe
 * can't happen at logout time, and why it waits for a tab to be alone.
 */
export const CACHE_WIPE_FLAG = 'spFirestoreCacheWipe';

// Persistent (IndexedDB) local cache. Without it every reload re-fetches the
// user doc, cart, notifications and catalog over the network before anything
// can render; with it those snapshots resolve from disk in ~20ms and the
// server value lands right after. Every onSnapshot consumer that cares about
// authority already checks `metadata.fromCache` (see auth-context.tsx).
//
// `persistentMultipleTabManager` is required because the app is routinely open
// in more than one tab (the email-verification flow deliberately opens a second
// one). The single-tab manager would throw `failed-precondition` there and drop
// us back to no cache at all.
//
// Falls back to the memory-only default when persistence is unavailable:
// Safari private browsing, blocked storage, or a browser with no IndexedDB.
// That path is strictly the old behaviour, so it degrades to "slow", not
// "broken".
//
// Blocked storage has to be caught BEFORE initializing. initializeFirestore
// never throws for it: the tab manager first reads `window.localStorage`
// inside the SDK's own async start-up, and with site data blocked that read
// throws a SecurityError, which is not one of the errors the SDK falls back to
// memory for. The instance was left dead ("INTERNAL UNHANDLED ERROR"), the
// first listener then hit an internal assertion, and a product page went to
// the global error screen. browserLocalStorage() is null exactly when that
// read throws, and IndexedDB cannot be usable where localStorage is blocked.
function createFirestore(): Firestore {
    if (typeof window === 'undefined' || !isNewApp) {
        // Server render, or a repeat module evaluation (HMR) where
        // initializeFirestore would throw because the instance already exists.
        return getFirestore(firebaseApp);
    }
    if (browserLocalStorage() === null || typeof indexedDB === 'undefined') {
        return getFirestore(firebaseApp);
    }
    try {
        return initializeFirestore(firebaseApp, {
            localCache: persistentLocalCache({
                tabManager: persistentMultipleTabManager(),
            }),
        });
    } catch {
        return getFirestore(firebaseApp);
    }
}

const firestore: Firestore = createFirestore();

/**
 * Drop everything the previous session cached to disk.
 *
 * This runs at BOOT rather than inside `logout()` on purpose. Clearing
 * IndexedDB requires the Firestore instance to be stopped (`terminate()`), and
 * a terminated instance stays dead for the rest of the SPA session: logging
 * out and then browsing to /marketplace without a reload would find a Firestore
 * that can never serve another read. Deferring to the next boot lets us clear
 * before a single listener has attached.
 *
 * ONLY A TAB THAT IS ALONE CLEARS. The cache is one database shared by every
 * open tab, and deleting it under another tab is not refused: Firestore in that
 * tab shuts itself down, silently, until the tab is reloaded. A flagged boot
 * with another tab open therefore leaves the flag for a later boot. The lock
 * that proves a tab is alone, and the incident behind it, are in
 * src/lib/firestore-cache-wipe.ts. The flag is only cleared by a wipe that
 * succeeds.
 *
 * Residual exposure: between sign-out and the wipe, the previous user's
 * documents remain on disk. Nothing in the app reads them (every listener is
 * gated on an authenticated uid, and security rules would reject a fresh read
 * anyway), but they are recoverable by someone with local devtools access until
 * a boot finds no other tab open and clears them.
 */
function wipeCacheIfFlagged(): void {
    if (typeof window === 'undefined') return;
    let storage: Storage | null = null;
    try {
        storage = window.localStorage;
    } catch {
        // Storage blocked (Safari ITP, privacy modes): no flag was written.
    }
    void wipeFlaggedCacheWhenAlone({
        flagKey: CACHE_WIPE_FLAG,
        storage,
        locks: 'locks' in navigator ? navigator.locks : null,
        clearCache: () => clearIndexedDbPersistence(firestore),
    });
}

wipeCacheIfFlagged();

// Storage's SDK and client wait for the first upload (see AppStorage in
// src/lib/storage.ts); the app is all the context needs to hand on.
const storage: AppStorage = { app: firebaseApp };

export { firebaseApp, auth, firestore, storage };
